import { z } from 'zod'

/**
 * Machine-readable reasons on refusals the app reacts to, rather than only shows.
 *
 * The wire still accepts any string (`apiErrorBodySchema`), so a code this
 * version does not know yet is ignored instead of failing the parse. Throw
 * sites and the checks that branch on a code both use this object, so the
 * spelling cannot drift between them.
 */
export const API_ERROR_CODE = {
  billingLocked: 'billing_locked',
  legalAcceptanceRequired: 'legal_acceptance_required',
  legalVersionOutdated: 'legal_version_outdated',
  emailTaken: 'email_taken',
  signupClosed: 'signup_closed',
  vendorExists: 'vendor_exists',
  /** Signing up with an email that a pending invitation already holds: the invitation is the way in. */
  invitePending: 'invite_pending',
  /** A vendor company has asked as many venues to work with it today as it may (`VENUE_ASKS_PER_DAY`). */
  venueAskLimit: 'venue_ask_limit',
} as const

/**
 * The `error.flatten()` object `ZodValidationPipe` puts on a schema rejection,
 * with the rejection's issue codes.
 *
 * `formErrors` are issues with no path; `fieldErrors` maps each top-level field
 * to its messages. Unknown keys are ignored so a later Zod can add one without
 * failing every client that is still running.
 */
export const apiErrorDetailsSchema = z.object({
  formErrors: z.array(z.string()),
  fieldErrors: z.record(z.string(), z.array(z.string())),
  /**
   * Zod's own code for each issue. `too_big` and `too_small` are constraints on
   * what was typed; any other code means the client built a payload the API
   * could not read. Any string, so a code this version does not know yet cannot
   * make the whole error unreadable.
   */
  issueCodes: z.array(z.string()).optional(),
})

/**
 * A JSON error envelope. Extra keys are ignored: Nest adds `statusCode` and
 * `error`, and a duplicate vendor also carries `vendorId`, which callers that
 * only need the code do not have to know about.
 */
export const apiErrorBodySchema = z.object({
  message: z.string().optional(),
  error: z.string().optional(),
  statusCode: z.number().optional(),
  /**
   * Present only on a schema rejection — `ZodValidationPipe` is the one thing in
   * the API that attaches it, and it attaches `error.flatten()` with Zod's
   * issue codes. That makes it the difference between the two kinds of 400: a
   * payload this client built wrongly, which is a bug, and a refusal written for
   * a person to read, which is the server doing its job.
   */
  details: apiErrorDetailsSchema.optional(),
  /** See {@link API_ERROR_CODE}. Any other string is left for the caller to ignore. */
  code: z.string().optional(),
})

export type ApiErrorBody = z.infer<typeof apiErrorBodySchema>
